import { useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from "react"
import type { JournalEntry, VehicleStatus } from "../types/api"
import { useI18n } from "../hooks/useI18n"
import { garageUi } from "../i18n/garageUi"
import { localizeSignalTitle } from "../i18n/signalTitles"
import { localizeStatusTitle } from "../i18n/statusTitles"
import { FigmaSignalGlyph, hasExactFigmaSignalGlyph } from "./FigmaSignalGlyphLibrary"
import "./GarageFeatureSheets.css"
import { FINAL_STATUS_VISUALS } from "./StatusVisuals"

const FINAL_LOGBOOK_TONE_BY_SLUG: Record<string, string> = {
  // Exact Logbook Sheet tone assignments from the approved Figma examples.
  no_turn_signal: "var(--bb-figma-signal-yellow-1)",
  bad_parking: "var(--bb-figma-signal-yellow-1)",
  cut_off: "var(--bb-figma-signal-yellow-2)",
  ran_red_light: "var(--bb-figma-signal-yellow-3)",
  thanks: "var(--bb-figma-signal-green-1)",
  thanks_for_help: "var(--bb-figma-signal-green-2)",
  nice_plate: "var(--bb-figma-signal-green-3)",
  window_open: "var(--bb-figma-signal-red-1)",
  urgent_contact_now: "var(--bb-figma-signal-red-2)",
  car_being_towed: "var(--bb-figma-signal-red-3)",
}

function formatJournalTimestamp(value: string, locale: string) {
  const date = new Date(value)
  return {
    date: new Intl.DateTimeFormat(locale, {
      day: "2-digit", month: "2-digit", year: "numeric",
    }).format(date),
    time: new Intl.DateTimeFormat(locale, {
      hour: "2-digit", minute: "2-digit",
    }).format(date),
  }
}

function resolveLogbookTone(entry: JournalEntry): string {
  const slug = entry.signal_type?.slug || ""
  return FINAL_LOGBOOK_TONE_BY_SLUG[slug] || entry.signal_type?.color || "var(--bb-figma-border-strong)"
}

function useGrabberDrag(onClose: () => void) {
  const dragStartYRef = useRef<number | null>(null)
  const dragOffsetRef = useRef(0)
  const [dragOffset, setDragOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)

  const setSheetDragOffset = (value: number) => {
    const next = Math.max(0, value)
    dragOffsetRef.current = next
    setDragOffset(next)
  }

  const onPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return
    dragStartYRef.current = event.clientY
    setSheetDragOffset(0)
    setIsDragging(true)
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const onPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    if (dragStartYRef.current === null) return
    event.preventDefault()
    setSheetDragOffset(event.clientY - dragStartYRef.current)
  }

  const finish = (event: ReactPointerEvent<HTMLElement>, allowClose: boolean) => {
    const shouldClose = allowClose && dragOffsetRef.current >= 72
    dragStartYRef.current = null
    setSheetDragOffset(0)
    setIsDragging(false)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    if (shouldClose) onClose()
  }

  return { dragOffset, isDragging, onPointerDown, onPointerMove, finish }
}

export function GarageLogbookSheet({
  isIOS, entries, devScrollState, onBack, onReply,
}: {
  isIOS: boolean
  entries: JournalEntry[]
  devScrollState?: "start" | "scrolled" | null
  onBack: () => void
  onReply: (entry: JournalEntry) => void
}) {
  const { locale, direction } = useI18n()
  const viewportRef = useRef<HTMLDivElement>(null)
  const [scrollState, setScrollState] = useState({ top: true, bottom: false })
  const drag = useGrabberDrag(onBack)

  useEffect(() => {
    if (!import.meta.env.DEV || !devScrollState) return
    const node = viewportRef.current
    if (!node) return
    node.scrollTop = devScrollState === "scrolled" ? 29 : 0
    setScrollState({
      top: node.scrollTop <= 1,
      bottom: node.scrollTop + node.clientHeight >= node.scrollHeight - 1,
    })
  }, [devScrollState, entries])
  return (
    <div className={`bbf-feature-overlay logbook-overlay ${isIOS ? "platform-ios" : "platform-android"}`} onClick={onBack}>
      <section
        className={`bbf-feature-sheet logbook${drag.isDragging ? " is-dragging" : ""}`}
        style={{ "--bbf-feature-drag-y": `${drag.dragOffset}px` } as CSSProperties}
        onClick={(event) => event.stopPropagation()}
      >
        <i
          className="bbf-feature-grabber"
          onPointerDown={drag.onPointerDown}
          onPointerMove={drag.onPointerMove}
          onPointerUp={(event) => drag.finish(event, true)}
          onPointerCancel={(event) => drag.finish(event, false)}
        />
        <h2 dir={direction}>{garageUi(locale, "action.logbook")}</h2>
        <div
          ref={viewportRef}
          className="bbf-feature-scroll logbook-list"
          onScroll={(event) => {
            const node = event.currentTarget
            setScrollState({
              top: node.scrollTop <= 1,
              bottom: node.scrollTop + node.clientHeight >= node.scrollHeight - 1,
            })
          }}
        >
          {entries.map((entry) => {
            const rawTitle = entry.entry_type === "signal" ? entry.signal_type?.title : entry.title
            const title = entry.entry_type === "signal" ? localizeSignalTitle(locale, rawTitle) : rawTitle
            const signalCategory = entry.signal_type?.category
            const timestamp = formatJournalTimestamp(entry.created_at, locale)
            const canReply = Boolean(entry.entry_type === "signal" && entry.allow_reply && entry.reply_token)
            const hasFigmaIcon = entry.entry_type === "signal" &&
              (signalCategory === "urgent" || signalCategory === "road" || signalCategory === "attention") &&
              hasExactFigmaSignalGlyph(signalCategory, entry.signal_type?.slug)
            return (
              <article
                className={`bbf-logbook-row ${canReply ? "has-reply" : ""}`}
                key={`${entry.entry_type}-${entry.id}`}
                style={{ "--bb-logbook-tone": resolveLogbookTone(entry) } as CSSProperties}
              >
                <span className="bbf-logbook-icon">
                  {hasFigmaIcon && signalCategory
                    ? <FigmaSignalGlyph category={signalCategory as "urgent" | "road" | "attention"} slug={entry.signal_type?.slug} />
                    : entry.entry_type === "signal" && entry.signal_type?.icon
                      ? <span aria-hidden="true">{entry.signal_type.icon}</span>
                      : <span aria-hidden="true">•</span>}
                </span>
                <span className="bbf-logbook-copy" dir={direction}>
                  <strong>{title || garageUi(locale, "feature.garageEvent")}</strong>
                  <small>
                    <span className="bbf-logbook-date">{timestamp.date}</span>
                    <span className="bbf-logbook-time">{timestamp.time}</span>
                  </small>
                </span>
                {canReply && (
                  <button type="button" onClick={() => onReply(entry)} dir={direction}>{garageUi(locale, "feature.reply")}</button>
                )}
              </article>
            )
          })}
        </div>
        {!scrollState.top && <div className="bbf-logbook-fade top" />}
        {!scrollState.bottom && <div className="bbf-logbook-fade bottom" />}
      </section>
    </div>
  )
}

export function GarageStatusSheet({
  isIOS, statuses, currentStatusId, selectedStatusId, busy, onSelect, onSuggestOwn, onBack, onApply,
}: {
  isIOS: boolean
  statuses: VehicleStatus[]
  currentStatusId: number | null
  selectedStatusId: number | null | undefined
  busy: boolean
  onSelect: (id: number | null) => void
  onSuggestOwn: () => void
  onBack: () => void
  onApply: () => void
}) {
  const { locale, direction, t } = useI18n()
  const choices: Array<VehicleStatus | { id: null; slug?: never; title: string; color: string } | { id: "suggest-own"; slug: "suggest-own"; title: string; color: string; actionOnly: true }> = [
    { id: null, title: garageUi(locale, "feature.noStatus"), color: "var(--bb-figma-plate-light)" },
    ...statuses,
    { id: "suggest-own", slug: "suggest-own", title: t("signalSelection.suggestOwn"), color: "var(--bb-figma-surface-signal-choice)", actionOnly: true },
  ]
  const [scrollState, setScrollState] = useState({ top: true, bottom: false })
  const drag = useGrabberDrag(onBack)
  return (
    <div className={`bbf-feature-overlay status-overlay ${isIOS ? "platform-ios" : "platform-android"}`} onClick={onBack}>
      <section
        className={`bbf-feature-sheet status${drag.isDragging ? " is-dragging" : ""}`}
        style={{ "--bbf-feature-drag-y": `${drag.dragOffset}px` } as CSSProperties}
        onClick={(event) => event.stopPropagation()}
      >
        <i
          className="bbf-feature-grabber"
          onPointerDown={drag.onPointerDown}
          onPointerMove={drag.onPointerMove}
          onPointerUp={(event) => drag.finish(event, true)}
          onPointerCancel={(event) => drag.finish(event, false)}
        />
        <h2 dir={direction}>{garageUi(locale, "action.status")}</h2>
        <div
          className="bbf-feature-scroll status-list"
          onScroll={(event) => {
            const node = event.currentTarget
            setScrollState({
              top: node.scrollTop <= 1,
              bottom: node.scrollTop + node.clientHeight >= node.scrollHeight - 1,
            })
          }}
        >
          {choices.map((status) => {
            const actionOnly = "actionOnly" in status && status.actionOnly
            const selectableId: number | null | undefined = typeof status.id === "string" ? undefined : status.id
            const selected = selectableId !== undefined && selectedStatusId === selectableId
            const isSuggestOwn = status.id !== null && (status.slug === "suggest-own" || status.slug === "suggest_own")
            const approved = status.id === null ? null : FINAL_STATUS_VISUALS[status.slug || ""]
            const visualStyle = status.id === null ? {} : approved ? { "--status-start": approved.fill[0], "--status-end": approved.fill[1] } : { "--status-start": status.color || "var(--bb-figma-text-muted)", "--status-end": status.color || "var(--bb-figma-text-muted)" }
            // LOCKED PRODUCT RULE: choice capsules show localized status names only.
            // Frame copy belongs only to the number frame; AutoClan is localized here
            // while its actual frame label remains the registered clan name.
            const displayTitle = status.id === null || isSuggestOwn
              ? status.title
              : localizeStatusTitle(locale, status.slug, status.title).toLocaleUpperCase(locale)
            return (
              <button
                className={`bbf-status-choice ${selected ? "is-selected" : ""} ${status.id === null ? "is-empty" : ""} ${isSuggestOwn ? "is-suggest-own" : ""}`}
                key={status.id ?? "none"}
                onClick={actionOnly ? onSuggestOwn : selectableId === undefined ? undefined : () => onSelect(selectableId)}
                style={visualStyle as CSSProperties}
              >
                <span className="bbf-status-choice-icon" aria-hidden="true">
                  {status.id === null ? <span className="bbf-status-none-dash">—</span> : isSuggestOwn ? <span className="bbf-status-suggest-plus">+</span> : ""}
                </span>
                <span className="bbf-status-choice-label" dir={direction}>{displayTitle}</span>
              </button>
            )
          })}
        </div>
        {!scrollState.top && <div className="bbf-status-fade top" />}
        {!scrollState.bottom && <div className="bbf-status-fade bottom" />}
        <button className="bbf-feature-button back" onClick={onBack} dir={direction}>{t("common.back")}</button>
        <button
          className="bbf-feature-button apply"
          disabled={selectedStatusId === undefined || selectedStatusId === currentStatusId || busy}
          onClick={onApply}
          dir={direction}
        >{garageUi(locale, "feature.apply")}</button>
      </section>
    </div>
  )
}
